import { useCallback, useMemo, useRef } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import styled from "styled-components";

// #region SCOPED CSS
const StyledWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    width: ${({ $width }) => $width || "auto"};

    /* The visible custom trigger (child content or default trigger) */
    .drp-trigger {
        position: relative;
        cursor: pointer;
        width: 100%;
    }
    .drp-trigger.disabled,
    .drp-trigger.disabled * {
        cursor: not-allowed !important;
    }

    /* Hide the native antd RangePicker input — it sits on top as an invisible click target */
    .drp-hidden-picker {
        position: absolute;
        inset: 0;
        z-index: 2;
        opacity: 0;
    }
    .drp-hidden-picker .ant-picker {
        width: 100%;
        height: 100%;
        cursor: pointer;
    }

    /* Allow rightContent inside FormField (e.g., X clear button) to sit above the hidden picker */
    .drp-trigger .Frame48RightContent {
        position: relative;
        z-index: 3;
    }

    /* When disabled, block pointer events on the hidden picker */
    &.drp-disabled .drp-hidden-picker {
        pointer-events: none;
    }
`;
// #endregion

// #region CONSTANTS
const DEFAULT_FORMAT = "DD/MM/YYYY";
const DEFAULT_PLACEHOLDER = "Select date range";

const toAntdDate = date => {
    if (!date) return null;

    const parsedDate = moment.isMoment(date) ? date.clone() : moment(date.valueOf?.() ?? date);
    if (!parsedDate.isValid()) return null;

    return dayjs(parsedDate.valueOf());
};

/**
 * Predefined lock presets for quick usage.
 * Usage: <DateRangePicker disableAfter="today" />
 *        <DateRangePicker disableBefore="today" />
 */
const LOCK_RESOLVERS = {
    today: () => moment().endOf("day"),
    yesterday: () => moment().subtract(1, "day").endOf("day"),
    tomorrow: () => moment().add(1, "day").startOf("day"),
    startOfWeek: () => moment().startOf("week"),
    endOfWeek: () => moment().endOf("week"),
    startOfMonth: () => moment().startOf("month"),
    endOfMonth: () => moment().endOf("month"),
    startOfYear: () => moment().startOf("year"),
    endOfYear: () => moment().endOf("year")
};

/**
 * Resolve a lock value to a moment instance.
 * Accepts: "today" | "yesterday" | "tomorrow" | "startOfMonth" | ... | moment() | Date | "YYYY-MM-DD"
 */
const resolveLockDate = lockValue => {
    if (!lockValue) return null;
    if (typeof lockValue === "string" && LOCK_RESOLVERS[lockValue]) return LOCK_RESOLVERS[lockValue]();
    return moment(lockValue);
};
// #endregion

// #region COMPONENT DOCS
/**
 * DateRangePicker — A reusable, project-standard wrapper around Ant Design's DatePicker.RangePicker.
 *
 * @param {Object}          props
 *
 * --- Core ---
 * @param {[moment, moment]} [props.value]           - Controlled value as [startDate, endDate] (moment objects).
 * @param {Function}         [props.onChange]         - Called with (dates, dateStrings) when the range changes.
 * @param {string}           [props.format]          - Display/output format. Default: "YYYY-MM-DD".
 * @param {string}           [props.placeholder]     - Text shown when no range is selected.
 *
 * --- Date Locking ---
 * @param {string|moment|Date} [props.disableBefore] - Disable all dates before this date.
 *                                                     Accepts named presets: "today", "yesterday", "tomorrow",
 *                                                     "startOfWeek", "endOfWeek", "startOfMonth", "endOfMonth",
 *                                                     "startOfYear", "endOfYear".
 *                                                     Or a moment instance, Date, or date string.
 * @param {string|moment|Date} [props.disableAfter]  - Disable all dates after this date. Same formats as above.
 * @param {Function}           [props.disabledDate]  - Custom disabledDate function (receives current moment).
 *                                                     If provided together with disableBefore/disableAfter,
 *                                                     all constraints are combined (all must pass).
 *
 * --- Calendar Mode ---
 * @param {boolean}          [props.singleCalendar]  - If true, shows a single calendar panel instead of dual.
 *                                                     Default: false (dual calendar).
 *
 * --- Appearance ---
 * @param {boolean}          [props.disabled]        - Disables the picker.
 * @param {string}           [props.className]       - Additional CSS class on the wrapper.
 * @param {Object}           [props.style]           - Inline styles on the wrapper.
 * @param {string}           [props.width]           - CSS width for the wrapper (e.g. "235px", "100%").
 * @param {string}           [props.popupClassName]  - Additional CSS class on the calendar popup.
 * @param {Object}           [props.popupStyle]      - Inline styles on the calendar popup.
 * @param {Array}            [props.presets]          - Preset ranges for quick selection.
 * @param {boolean}          [props.allowClear]      - Show clear button. Default: true.
 * @param {React.ReactNode}  [props.children]        - Custom trigger content. If not provided, a default
 *                                                     trigger with calendar icon + placeholder text is rendered.
 *
 * --- Passthrough ---
 * @param {Object}           [props.pickerProps]     - Any additional props to pass directly to antd RangePicker.
 */
// #endregion

export default function DateRangePicker({
    // Core
    value,
    onChange,
    format = DEFAULT_FORMAT,
    placeholder = DEFAULT_PLACEHOLDER,

    // Date locking
    disableBefore,
    disableAfter,
    disabledDate: customDisabledDate,

    // Initial calendar month when empty
    defaultPickerValue,

    // Calendar mode
    singleCalendar = false,

    // Appearance
    disabled = false,
    className = "",
    style,
    width,
    popupClassName = "",
    popupStyle,
    presets,
    allowClear = true,
    children,

    // Passthrough
    pickerProps = {}
}) {
    // #region REFS
    const wrapperRef = useRef(null);
    // #endregion

    // #region METHODS
    /**
     * Combines disableBefore, disableAfter, and custom disabledDate into a single function.
     */
    const mergedDisabledDate = useCallback(
        current => {
            if (!current) return false;

            // Antd v5 passes dayjs objects — convert to moment for comparison
            const currentMoment = moment(current.valueOf());
            const beforeDate = resolveLockDate(disableBefore);
            const afterDate = resolveLockDate(disableAfter);

            if (beforeDate && currentMoment.isBefore(beforeDate, "day")) return true;
            if (afterDate && currentMoment.isAfter(afterDate, "day")) return true;
            if (customDisabledDate && customDisabledDate(currentMoment)) return true;

            return false;
        },
        [disableBefore, disableAfter, customDisabledDate]
    );

    const handleChange = useCallback(
        (dates, dateStrings) => {
            if (!onChange) return;

            if (!dates || !dates[0] || !dates[1]) {
                onChange(null, dateStrings);
                return;
            }

            // Antd v5 returns dayjs objects internally — convert to moment for project consistency
            const momentDates = [moment(dates[0].valueOf()), moment(dates[1].valueOf())];
            onChange(momentDates, dateStrings);
        },
        [onChange]
    );
    // #endregion

    // #region COMPUTED PROPS
    const formattedDisplay = useMemo(() => {
        if (!value || !value[0] || !value[1]) return null;
        const from = moment(value[0]).format(format);
        const to = moment(value[1]).format(format);
        return `${from} - ${to}`;
    }, [value, format]);

    const pickerValue = useMemo(() => {
        if (!value || !value[0] || !value[1]) return null;

        const from = toAntdDate(value[0]);
        const to = toAntdDate(value[1]);
        if (!from || !to) return null;

        return [from, to];
    }, [value]);

    const antdDefaultPickerValue = useMemo(() => {
        const target = defaultPickerValue || disableBefore;
        if (!target) return undefined;
        const resolved = resolveLockDate(target);
        if (!resolved) return undefined;
        const dayjsObj = toAntdDate(resolved);
        return dayjsObj ? [dayjsObj, dayjsObj] : undefined;
    }, [defaultPickerValue, disableBefore]);

    const singleCalendarClass = singleCalendar ? "drp-single-calendar" : "";
    const finalPopupClassName =
        `range-picker-pop-up ${singleCalendarClass} ${popupClassName} ${disabled ? "d-none" : ""}`.trim();
    // #endregion

    // #region RENDER
    return (
        <StyledWrapper
            ref={wrapperRef}
            className={`date-range-picker ${disabled ? "drp-disabled" : ""} ${className}`}
            style={style}
            $width={width}>
            {/* Invisible antd RangePicker — captures clicks and shows the calendar popup */}
            <div className="drp-hidden-picker">
                <DatePicker.RangePicker
                    value={pickerValue}
                    onChange={handleChange}
                    format={format}
                    disabledDate={mergedDisabledDate}
                    defaultPickerValue={antdDefaultPickerValue}
                    presets={presets}
                    allowClear={allowClear}
                    popupClassName={finalPopupClassName}
                    popupStyle={popupStyle}
                    getPopupContainer={() => wrapperRef.current || document.body}
                    style={{
                        opacity: 0,
                        width: "100%",
                        height: "100%"
                    }}
                    {...pickerProps}
                />
            </div>

            {/* Visible trigger — either custom children or the default trigger */}
            <div className={`drp-trigger ${disabled ? "disabled" : ""}`}>
                {children || (
                    <DefaultTrigger
                        displayText={formattedDisplay}
                        placeholder={placeholder}
                        disabled={disabled}
                        hasValue={!!formattedDisplay}
                    />
                )}
            </div>
        </StyledWrapper>
    );
    // #endregion
}

// #region SMALL COMPONENTS
const DefaultTriggerStyled = styled.div`
    background: var(--backgrounds-and-lines-n-400, #1e2631);
    border-radius: 4px;
    padding: 8px;
    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;
    justify-content: flex-start;
    height: 37px;
    width: 100%;
    border: 1px solid transparent;
    transition: border-color 0.2s ease;

    &.has-value {
        border-color: #0060cc;
    }
    &.disabled {
        cursor: not-allowed !important;
    }
    &.disabled i,
    &.disabled .drp-label {
        color: var(--text-icons-gray-300, #4f5b6d);
    }

    i {
        flex-shrink: 0;
        font-size: 12px;
    }

    .drp-label {
        color: var(--text-and-icons-gray-200, #7c868b);
        font-size: 12px;
        font-weight: 400;
        font-style: italic;
        flex: 1;
        line-height: normal;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &.has-value .drp-label {
        color: #ffffff;
        font-style: normal;
    }

    .drp-chevron {
        font-size: 10px;
        flex-shrink: 0;
    }
`;

const DefaultTrigger = ({ displayText, placeholder, disabled, hasValue }) => (
    <DefaultTriggerStyled className={`${disabled ? "disabled" : ""} ${hasValue ? "has-value" : ""}`}>
        <i className="fa-light fa-calendar" />
        <span className="drp-label">{displayText || placeholder}</span>
        <i className="fa-light fa-chevron-down drp-chevron" />
    </DefaultTriggerStyled>
);
// #endregion
